import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useQueries } from "@tanstack/react-query";
import { Plug } from "lucide-react";
import * as React from "react";

import {
  type AdminConnectorLicense,
  type AdminMemberConnectorAccess,
  listAdminOrgMemberConnectors,
} from "@ando/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Checkbox,
  cn,
  useToast,
} from "@ando/ui";

import {
  adminOrgKeys,
  useAdminOrgConnectors,
  useAdminOrgMembers,
  useSetAdminOrgConnectorLicense,
  useSetAdminOrgMemberConnector,
} from "@/features/admin/hooks/useAdminOrg";

import { ConnectorMatrix } from "./ConnectorMatrix";
import { BeGapBanner, LoadingRow } from "./_shared";

/**
 * Connectors tab — status matrix header + license grid (per connector,
 * on/off toggle) + member matrix for ``per_user`` connectors.
 *
 * v1 reference: ``OrgConnectorsTab.tsx`` (166 LoC) + the legacy
 * member×connector ``ConnectorMatrix`` (209 LoC). v2 keeps the same
 * shape but reads from the BE-canonical ``AdminConnectorLicense`` rows;
 * the per-member matrix only renders for ``assignment_mode = per_user``.
 *
 * NEW (May 2026): the tab header now shows ``<ConnectorMatrix>`` —
 * connector × lifecycle-state overview — so admins can spot OAuth
 * expirations / connection errors at-a-glance before drilling into the
 * per-member grid below.
 *
 * NEW (Jun 2026, P4): the matrix is now hydrated from BE on mount via
 * one ``GET /members/{userId}/connectors`` per member, fanned out with
 * ``useQueries``. The same responses provide the authoritative
 * ``display_name`` per ``connector_key`` so we no longer need a catalog
 * endpoint to title-case license cards. Column headers became bulk
 * toggles: click to flip every cell in that column ON/OFF; an
 * ``AlertDialog`` confirms when disabling more than 5 active assignees.
 */

export interface OrgConnectorsTabProps {
  orgId: string;
}

/** Title-case fallback when no BE display name is available. */
function titleizeKey(key: string): string {
  return key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) =>
      part.length <= 3 && part === part.toLowerCase()
        ? part.toUpperCase()
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join(" ");
}

export function OrgConnectorsTab({ orgId }: OrgConnectorsTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const connectorsQuery = useAdminOrgConnectors(orgId);
  const membersQuery = useAdminOrgMembers(orgId);
  const toggleLicense = useSetAdminOrgConnectorLicense(orgId);
  const toggleMember = useSetAdminOrgMemberConnector(orgId);

  const licenses = React.useMemo<AdminConnectorLicense[]>(
    () => connectorsQuery.data ?? [],
    [connectorsQuery.data],
  );
  const members = React.useMemo(() => membersQuery.data ?? [], [membersQuery.data]);
  const enabledLicenses = React.useMemo(() => licenses.filter((l) => l.is_enabled), [licenses]);
  const perUserLicenses = React.useMemo(
    () => enabledLicenses.filter((l) => l.assignment_mode === "per_user"),
    [enabledLicenses],
  );

  // ── Initial matrix hydration ───────────────────────────────────────────
  // Fan-out: one ``GET /members/{userId}/connectors`` per member. We use
  // ``useQueries`` (not N independent ``useQuery``) so React renders once
  // when all responses settle. Disabled until both ``members`` and
  // ``licenses`` resolved — otherwise we'd thrash N queries on mount.
  const memberAccessQueries = useQueries({
    queries: members.map((m) => ({
      queryKey: [...adminOrgKeys.connectors(orgId), "member-access", m.user_id] as const,
      queryFn: () => listAdminOrgMemberConnectors(orgId, m.user_id),
      enabled: members.length > 0 && licenses.length > 0,
      staleTime: 30_000,
    })),
  });

  const hydrationReady =
    members.length > 0 &&
    memberAccessQueries.length === members.length &&
    memberAccessQueries.every((q) => q.isSuccess || q.isError);

  // Build a fingerprint of each query's ``data`` identity so downstream
  // memos only recompute when an actual response changes. The wrapping
  // ``useQueries`` array is recreated each render, but ``dataUpdatedAt``
  // is a monotonic timestamp updated by TanStack only when a response
  // actually lands — joining them gives a primitive cache key.
  const dataFingerprint = memberAccessQueries
    .map((q) => (q.dataUpdatedAt ?? 0).toString())
    .join("|");

  // Merge the responses into a stable ``{ [userId]: Set<connectorKey> }``
  // map. Recomputed only when ``dataFingerprint`` changes, i.e. when a
  // per-member ``GET /connectors`` response actually lands.
  const hydratedAssignments = React.useMemo<Record<string, Set<string>>>(() => {
    const acc: Record<string, Set<string>> = {};
    members.forEach((m, idx) => {
      const rows = (memberAccessQueries[idx]?.data ?? []) as AdminMemberConnectorAccess[];
      acc[m.user_id] = new Set(rows.filter((r) => r.is_assigned).map((r) => r.connector_key));
    });
    return acc;
  }, [members, dataFingerprint, memberAccessQueries]);

  // Track local overrides on top of the BE-hydrated baseline. We
  // intentionally store the FULL effective map so toggles render
  // optimistically before TanStack invalidates the per-member query.
  const [overrides, setOverrides] = React.useState<Record<string, Set<string>>>({});

  // Reset overrides each time the BE baseline rehydrates — the new
  // baseline already reflects the just-mutated state.
  const baselineSignature = React.useMemo(
    () =>
      Object.entries(hydratedAssignments)
        .map(([uid, set]) => `${uid}:${[...set].sort().join(",")}`)
        .join("|"),
    [hydratedAssignments],
  );
  React.useEffect(() => {
    setOverrides({});
  }, [baselineSignature]);

  const assignments = React.useMemo<Record<string, Set<string>>>(() => {
    if (Object.keys(overrides).length === 0) return hydratedAssignments;
    const merged: Record<string, Set<string>> = {};
    members.forEach((m) => {
      merged[m.user_id] =
        overrides[m.user_id] ?? hydratedAssignments[m.user_id] ?? new Set<string>();
    });
    return merged;
  }, [overrides, hydratedAssignments, members]);

  // ── Display name resolver ──────────────────────────────────────────────
  // Build a ``connector_key → display_name`` map from member-access
  // responses (the BE includes ``display_name`` per row). Falls back to
  // a title-case of the slug when no member-row has been observed yet.
  const displayNameMap = React.useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const license of licenses) {
      if (license.display_name) map[license.connector_key] = license.display_name;
    }
    for (const q of memberAccessQueries) {
      const rows = (q.data ?? []) as AdminMemberConnectorAccess[];
      for (const r of rows) {
        if (r.display_name && !map[r.connector_key]) {
          map[r.connector_key] = r.display_name;
        }
      }
    }
    return map;
  }, [licenses, dataFingerprint, memberAccessQueries]);

  const resolveDisplayName = React.useCallback(
    (key: string): string => displayNameMap[key] ?? titleizeKey(key),
    [displayNameMap],
  );

  // ── License toggle ─────────────────────────────────────────────────────
  const handleLicenseToggle = (license: AdminConnectorLicense, next: boolean): void => {
    toggleLicense.mutate(
      { connectorKey: license.connector_key, is_enabled: next },
      {
        onSuccess: () => {
          const name = resolveDisplayName(license.connector_key);
          toast.success(i18n._(msg`Licenza aggiornata`), {
            description: next
              ? i18n._(msg`${name} abilitato.`)
              : i18n._(msg`${name} disabilitato.`),
          });
        },
        onError: (err) =>
          toast.error(i18n._(msg`Licenza connector`), {
            description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          }),
      },
    );
  };

  // ── Cell toggle ────────────────────────────────────────────────────────
  const handleCellToggle = (userId: string, connectorKey: string, next: boolean): void => {
    // Optimistic local update before the mutation lands.
    setOverrides((prev) => {
      const base = prev[userId] ?? assignments[userId] ?? new Set<string>();
      const set = new Set(base);
      if (next) set.add(connectorKey);
      else set.delete(connectorKey);
      return { ...prev, [userId]: set };
    });
    toggleMember.mutate(
      { userId, connectorKey, assigned: next },
      {
        onError: (err) => {
          // Rollback the optimistic flip.
          setOverrides((prev) => {
            const base = prev[userId] ?? assignments[userId] ?? new Set<string>();
            const set = new Set(base);
            if (next) set.delete(connectorKey);
            else set.add(connectorKey);
            return { ...prev, [userId]: set };
          });
          toast.error(i18n._(msg`Accesso connector`), {
            description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          });
        },
      },
    );
  };

  // ── Column-bulk toggle ─────────────────────────────────────────────────
  // Counts current ON cells in a column; if disabling more than 5 active
  // members, prompts the admin via AlertDialog before firing. Otherwise
  // fires N per-member mutations in parallel.
  const countAssignedInColumn = React.useCallback(
    (connectorKey: string): number => {
      let n = 0;
      for (const m of members) {
        if (assignments[m.user_id]?.has(connectorKey)) n += 1;
      }
      return n;
    },
    [assignments, members],
  );

  const applyColumnBulkToggle = React.useCallback(
    (connectorKey: string, next: boolean): void => {
      // Fan-out per-member mutations. Optimistic local state first so the
      // checkboxes flip immediately even before all PUTs resolve.
      setOverrides((prev) => {
        const merged: Record<string, Set<string>> = { ...prev };
        for (const m of members) {
          const base = merged[m.user_id] ?? assignments[m.user_id] ?? new Set<string>();
          const set = new Set(base);
          if (next) set.add(connectorKey);
          else set.delete(connectorKey);
          merged[m.user_id] = set;
        }
        return merged;
      });
      for (const m of members) {
        const current = assignments[m.user_id]?.has(connectorKey) ?? false;
        if (current === next) continue;
        toggleMember.mutate(
          { userId: m.user_id, connectorKey, assigned: next },
          {
            onError: (err) =>
              toast.error(i18n._(msg`Accesso connector`), {
                description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
              }),
          },
        );
      }
      const name = resolveDisplayName(connectorKey);
      toast.success(i18n._(msg`Colonna aggiornata`), {
        description: next
          ? i18n._(msg`${name} abilitato per tutti i membri.`)
          : i18n._(msg`${name} disabilitato per tutti i membri.`),
      });
    },
    [assignments, members, resolveDisplayName, toggleMember, toast, i18n],
  );

  const [bulkConfirm, setBulkConfirm] = React.useState<{
    connectorKey: string;
    next: boolean;
    affected: number;
  } | null>(null);

  const handleColumnHeaderClick = (connectorKey: string): void => {
    if (members.length === 0) return;
    const assigned = countAssignedInColumn(connectorKey);
    // If most members are already ON, the click should disable; otherwise
    // enable. Threshold = strict majority (>= 1) → next = !any-assigned.
    const next = assigned < members.length;
    // Guard: disabling > 5 active members → confirm first.
    if (!next && assigned > 5) {
      setBulkConfirm({ connectorKey, next, affected: assigned });
      return;
    }
    applyColumnBulkToggle(connectorKey, next);
  };

  if (connectorsQuery.isLoading) {
    return <LoadingRow label={i18n._(msg`Caricamento connector…`)} />;
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Status matrix — connector × lifecycle state. Header view so the
          admin can spot OAuth expirations / errors before drilling into
          the license list below. */}
      <ConnectorMatrix licenses={licenses} displayName={resolveDisplayName} />

      {/* License grid */}
      <section
        aria-labelledby="org-connectors-licenses"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <div>
            <h2
              id="org-connectors-licenses"
              className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
            >
              {i18n._(msg`Licenze connector`)}
            </h2>
            <p className="mt-1 text-[12px] text-ink-3">
              {i18n._(msg`${enabledLicenses.length} connector licenziati`)}
            </p>
            <p className="mt-1 text-[11px] text-ink-3">
              {i18n._(
                msg`I connector disattivati restano visibili nel catalogo e non sono utilizzabili dagli utenti finché non li abiliti.`,
              )}
            </p>
          </div>
        </div>
        {licenses.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-ink-3">
            <Plug className="mx-auto mb-2 h-5 w-5 opacity-40" aria-hidden />
            {i18n._(msg`Nessun connector configurato per questa organizzazione.`)}
          </p>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {licenses.map((l) => {
              const assignedCount = countAssignedInColumn(l.connector_key);
              return (
                <label
                  key={l.connector_key}
                  className={cn(
                    "flex cursor-pointer items-start justify-between gap-3 rounded-lg border p-3 transition-colors",
                    l.is_enabled
                      ? "border-accent/40 bg-ando-orange-soft/20"
                      : "border-hairline hover:border-accent/30 hover:bg-ando-orange-soft/10",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-[12.5px] text-ink-1">
                      {resolveDisplayName(l.connector_key)}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Badge variant="outline" className="text-[10px]">
                        {l.assignment_mode === "all_members"
                          ? i18n._(msg`Tutti i membri`)
                          : i18n._(msg`Per utente`)}
                      </Badge>
                      {l.is_enabled && l.assignment_mode === "per_user" && (
                        <Badge variant="secondary" className="text-[10px]">
                          {i18n._(msg`${assignedCount}/${members.length} assegnati`)}
                        </Badge>
                      )}
                      {l.is_enabled && l.assignment_mode === "per_user" && l.licensed_seats > 0 && (
                        <Badge variant="ghost" className="text-[10px]">
                          {i18n._(msg`${l.licensed_seats} posti`)}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Checkbox
                    className="mt-1"
                    role="switch"
                    aria-label={i18n._(msg`Licenza ${resolveDisplayName(l.connector_key)}`)}
                    checked={l.is_enabled}
                    onChange={(e) => handleLicenseToggle(l, e.target.checked)}
                  />
                </label>
              );
            })}
          </div>
        )}
      </section>

      {/* Member matrix */}
      <section
        aria-labelledby="org-connectors-matrix"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <div className="mb-3">
          <h2
            id="org-connectors-matrix"
            className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
          >
            {i18n._(msg`Accesso per membro`)}
          </h2>
          <p className="mt-1 text-[12px] text-ink-3">
            {i18n._(
              msg`Una riga per membro, una colonna per ogni connector «Per utente». Clicca l'intestazione di una colonna per attivare o disattivare in blocco.`,
            )}
          </p>
        </div>
        {perUserLicenses.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-ink-3">
            {i18n._(msg`Nessun connector con assegnazione «Per utente» abilitato.`)}
          </p>
        ) : members.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-ink-3">
            {i18n._(msg`Nessun membro nell'organizzazione.`)}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-hairline border-b">
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-card/55 px-3 py-2 text-left font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
                  >
                    {i18n._(msg`Membro`)}
                  </th>
                  {perUserLicenses.map((l) => {
                    const assigned = countAssignedInColumn(l.connector_key);
                    return (
                      <th
                        key={l.connector_key}
                        scope="col"
                        className="px-3 py-2 text-center font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
                      >
                        <button
                          type="button"
                          onClick={() => handleColumnHeaderClick(l.connector_key)}
                          aria-label={i18n._(
                            msg`Attiva/disattiva ${resolveDisplayName(
                              l.connector_key,
                            )} per tutti (attualmente ${assigned}/${members.length})`,
                          )}
                          className="inline-flex flex-col items-center gap-0.5 rounded-sm px-1.5 py-0.5 transition-colors hover:bg-ando-orange-soft/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                        >
                          <span className="text-ink-2 normal-case tracking-normal">
                            {resolveDisplayName(l.connector_key)}
                          </span>
                          <span className="font-mono text-[9.5px] text-ink-3 normal-case tracking-normal">
                            {assigned}/{members.length}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.user_id} className="border-hairline/60 border-b last:border-b-0">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-card/55 px-3 py-2 text-left text-ink-1"
                    >
                      <span className="truncate">{m.name ?? m.email ?? m.user_id}</span>
                    </th>
                    {perUserLicenses.map((l) => {
                      const isAssigned = assignments[m.user_id]?.has(l.connector_key) ?? false;
                      return (
                        <td key={l.connector_key} className="px-3 py-2 text-center">
                          <Checkbox
                            aria-label={i18n._(
                              msg`${resolveDisplayName(l.connector_key)} per ${m.email ?? m.user_id}`,
                            )}
                            checked={isAssigned}
                            onChange={(e) =>
                              handleCellToggle(m.user_id, l.connector_key, e.target.checked)
                            }
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {hydrationReady ? (
        <BeGapBanner
          message={
            <>
              {i18n._(
                msg`Matrice idratata dal BE: lo stato iniziale di ogni cella viene caricato da`,
              )}{" "}
              <code className="font-mono">
                GET /platform-admin/organizations/{"{id}"}/members/{"{userId}"}
                /connectors
              </code>{" "}
              {i18n._(msg`al mount.`)}
            </>
          }
        />
      ) : (
        <BeGapBanner
          message={
            <>
              {i18n._(msg`Idratazione della matrice in corso… una richiesta per membro`)} (
              <code className="font-mono">GET …/members/{"{userId}"}/connectors</code>).
            </>
          }
        />
      )}

      <AlertDialog
        open={bulkConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setBulkConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{i18n._(msg`Disabilitare in blocco?`)}</AlertDialogTitle>
            <AlertDialogDescription>
              {i18n._(msg`Stai per revocare l'accesso a`)}{" "}
              <strong>{bulkConfirm ? resolveDisplayName(bulkConfirm.connectorKey) : ""}</strong>{" "}
              {i18n._(msg`per`)} <strong>{bulkConfirm?.affected ?? 0}</strong>{" "}
              {i18n._(msg`membri. Vuoi continuare?`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{i18n._(msg`Annulla`)}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!bulkConfirm) return;
                applyColumnBulkToggle(bulkConfirm.connectorKey, bulkConfirm.next);
                setBulkConfirm(null);
              }}
            >
              {i18n._(msg`Disabilita per tutti`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
